// A Harvey ball: a circle filled clockwise from the top by a share of 0..1,
// read as "this much of the answer scale in place" (Kenneth, 2026-10-28:
// "replace some of the scores with harvey balls/circles to show 0-100% in
// place"). It draws a share the caller took from the report model through
// lib/share.ts; it computes nothing else. An incomplete area gets an empty,
// dashed ball; a not-applicable area a dotted one; both say so in the label.
//
// Printed as inline SVG, so the browser's print-to-PDF keeps the fill
// (theme.css: .harvey keeps print colours exact).

export type BallState = "share" | "incomplete" | "not_applicable";

export function HarveyBall({
  share,
  state = "share",
  size = 28,
  label,
}: {
  share: number | null;
  state?: BallState;
  size?: number;
  label: string;
}) {
  const r = size / 2 - 1.5;
  const c = size / 2;
  const s = state === "share" && share !== null ? Math.max(0, Math.min(1, share)) : 0;
  let slice: string | null = null;
  if (s >= 0.9995) slice = "full";
  else if (s > 0.0005) {
    const a = 2 * Math.PI * s;
    const x = c + r * Math.sin(a);
    const y = c - r * Math.cos(a);
    const large = s > 0.5 ? 1 : 0;
    slice = `M ${c} ${c} L ${c} ${c - r} A ${r} ${r} 0 ${large} 1 ${x.toFixed(3)} ${y.toFixed(3)} Z`;
  }
  const dash = state === "incomplete" ? "4 3" : state === "not_applicable" ? "1.5 3" : undefined;
  return (
    <svg
      className="harvey"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      style={{ flex: "none", verticalAlign: "middle" }}
    >
      <circle cx={c} cy={c} r={r} fill={state === "share" ? "var(--lf-muted-40, #e8ecf1)" : "none"} stroke="var(--text-secondary, #4a5a70)" strokeWidth="1.5" strokeDasharray={dash} />
      {slice === "full" && <circle cx={c} cy={c} r={r} fill="var(--accent, #264060)" />}
      {slice && slice !== "full" && <path d={slice} fill="var(--accent, #264060)" />}
    </svg>
  );
}
